<template>
  <div class="h-screen flex flex-col overflow-hidden bg-slate-50">
    <!-- Header -->
    <header
      class="flex-shrink-0 z-50 w-full border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60 shadow-sm">
      <div class="flex h-14 items-center justify-between px-4 md:px-6">
        <!-- Left side: Menu buttons and Logo -->
        <div class="flex items-center">
          <!-- Mobile menu button -->
          <Button variant="ghost" size="icon" class="md:hidden justify-center mr-2" @click="toggleSidebar">
            <Menu class="h-5 w-5" />
            <span class="sr-only">Toggle Menu</span>
          </Button>

          <!-- Desktop sidebar toggle -->
          <Button variant="ghost" size="icon" class="hidden md:flex mr-4 justify-center" @click="toggleSidebar">
            <Menu class="h-5 w-5" />
          </Button>

          <!-- Logo ZENTRY -->
          <router-link to="/dashboard" class="flex items-center group relative h-9">
            <!-- Isotipo Z (visible solo en móvil) -->
            <img src="@/assets/isotipo.png" alt="ZENTRY"
              class="h-6 w-auto object-contain drop-shadow-md transition-opacity duration-300 sm:opacity-0 sm:absolute sm:pointer-events-none" />
            <!-- Logo completo (visible solo en desktop) -->
            <img src="@/assets/logo_only.png" alt="ZENTRY"
              class="h-6 w-auto object-contain drop-shadow-md transition-all duration-300 opacity-0 absolute pointer-events-none sm:opacity-100 sm:relative sm:pointer-events-auto group-hover:scale-105" />
          </router-link>
        </div>

        <!-- Center: Company Selector -->
        <div class="flex-1 flex justify-center px-4">
          <DropdownMenu>
            <DropdownMenuTrigger as-child>
              <Button variant="outline" role="combobox" :disabled="loadingCompanies"
                class="w-[200px] md:w-[260px] h-10 justify-between bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 shadow-sm rounded-lg pl-2 pr-2">
                <div class="flex items-center truncate gap-2">
                  <!-- Company Initial Avatar -->
                  <div
                    class="h-6 w-6 rounded bg-gradient-to-br from-[#0099FF] to-[#0066CC] flex items-center justify-center shrink-0 shadow-sm">
                    <span class="text-white font-bold text-xs">{{ selectedCompanyInitials }}</span>
                  </div>
                  <span class="truncate text-sm font-semibold text-gray-700">{{ selectedCompanyName }}</span>
                </div>
                <ChevronsUpDown class="ml-2 h-3.5 w-3.5 shrink-0 text-gray-400" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent class="w-[200px] md:w-[260px] p-1 mt-1 rounded-lg border-gray-100 shadow-lg"
              align="center">
              <div class="px-2 py-1.5">
                <span class="text-[10px] text-slate-500 font-bold uppercase tracking-wider ml-1">Mis Empresas</span>
              </div>

              <div
                class="max-h-[250px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent">
                <DropdownMenuItem v-for="company in companies" :key="company.id"
                  @click="handleCompanySelect(company.id)"
                  class="cursor-pointer flex items-center px-2 py-1.5 rounded-md mb-0.5 focus:bg-transparent">
                  <div class="w-5 flex items-center justify-center mr-2">
                    <Check v-if="selectedCompanyId === company.id" class="h-3.5 w-3.5 text-[#009CA6]" />
                  </div>
                  <span :class="cn(
                    'truncate flex-1 font-medium text-sm',
                    selectedCompanyId === company.id ? 'text-[#009CA6]' : 'text-slate-600'
                  )">
                    {{ company.nombre_comercial || company.razon_social }}
                  </span>
                </DropdownMenuItem>
              </div>

              <DropdownMenuSeparator class="my-1 bg-gray-100" />

              <DropdownMenuItem @click="navigateToCompanies"
                class="cursor-pointer flex items-center px-2 py-1.5 mt-0.5 rounded-md text-[#009CA6] hover:text-[#008C96] hover:bg-[#009CA6]/5 transition-colors duration-200 focus:bg-[#009CA6]/5 focus:text-[#009CA6]">
                <div class="w-5 flex items-center justify-center mr-2">
                  <Plus class="h-3.5 w-3.5" />
                </div>
                <span class="font-medium text-sm">Gestionar Empresas</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <!-- Right side: User Menu - Completamente a la derecha -->
        <div class="flex items-center">
          <!-- User Menu -->
          <DropdownMenu>
            <DropdownMenuTrigger as-child>
              <Button variant="ghost"
                class="relative h-10 w-10 rounded-full border-2 border-transparent hover:border-primary/20 hover:bg-primary/5 shadow-sm hover:shadow-md transition-all duration-200 p-0 overflow-hidden">
                <Avatar class="h-9 w-9">
                  <AvatarImage :src="userAvatar" :alt="userName" class="object-cover" />
                  <AvatarFallback
                    class="bg-gradient-to-br from-blue-500 to-purple-600 text-white font-semibold text-sm">
                    {{ userInitials }}
                  </AvatarFallback>
                </Avatar>
                <!-- Indicador de usuario activo -->
                <div class="absolute bottom-0 right-0 h-3 w-3 bg-green-500 border-2 border-white rounded-full"></div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent class="w-64" align="end" :side-offset="8">
              <DropdownMenuLabel class="font-normal">
                <div class="flex flex-col space-y-1">
                  <p class="text-sm font-medium leading-none">{{ userName }}</p>
                  <p class="text-xs leading-none text-muted-foreground">{{ userEmail }}</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <DropdownMenuItem @click="navigateToProfile" class="cursor-pointer">
                  <User class="mr-3 h-4 w-4" />
                  <span>Perfil</span>
                  <span class="ml-auto text-xs text-muted-foreground">⌘P</span>
                </DropdownMenuItem>
                <DropdownMenuItem @click="navigateToSettings" class="cursor-pointer">
                  <Settings class="mr-3 h-4 w-4" />
                  <span>Configuración</span>
                  <span class="ml-auto text-xs text-muted-foreground">⌘S</span>
                </DropdownMenuItem>
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuItem @click="logout" class="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50">
                <LogOut class="mr-3 h-4 w-4" />
                <span>Cerrar Sesión</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>

    <div class="flex flex-1 overflow-hidden relative">
      <!-- Helper de espaciado fantasma -->
      <div :class="cn(
        'hidden md:block flex-shrink-0 transition-all duration-300 ease-in-out',
        sidebarCollapsed ? 'w-16' : 'w-64'
      )"></div>

      <!-- Sidebar Desktop Flotante/Fijo -->
      <aside 
        @mouseenter="isHoveringSidebar = true"
        @mouseleave="isHoveringSidebar = false"
        :class="cn(
        'hidden md:flex flex-col flex-shrink-0 border-r bg-white transition-all duration-300 ease-in-out overflow-hidden z-20',
        'fixed top-14 bottom-0 left-0',
        sidebarCollapsed && !isHoveringSidebar ? 'w-16' : 'w-64',
        sidebarCollapsed && isHoveringSidebar ? 'shadow-[4px_0_24px_rgba(0,0,0,0.15)]' : 'shadow-[2px_0_8px_-3px_rgba(0,0,0,0.1)]'
      )">
        <nav :class="cn(
          'flex-1 overflow-y-auto overflow-x-hidden py-4 scrollbar-soft',
          sidebarCollapsed && !isHoveringSidebar ? 'px-1' : 'px-3'
        )">
          <!-- Si el menú está colapsado y NO estamos haciendo hover, pasamos collapsed=true a SidebarNav -->
          <SidebarNav :collapsed="sidebarCollapsed && !isHoveringSidebar" />
        </nav>
      </aside>

      <!-- Mobile Sidebar -->
      <aside v-if="sidebarOpen"
        class="fixed inset-y-0 left-0 z-40 w-64 border-r bg-white md:hidden top-14 bottom-0 overflow-hidden shadow-xl">
        <nav class="h-full overflow-y-auto overflow-x-hidden px-3 py-4 scrollbar-soft">
          <SidebarNav :collapsed="false" />
        </nav>
      </aside>

      <!-- Overlay for mobile -->
      <div v-if="sidebarOpen" class="fixed inset-0 z-30 bg-background/80 backdrop-blur-sm md:hidden"
        @click="closeSidebar" />

      <!-- Main Content -->
      <main class="flex-1 overflow-y-auto overflow-x-hidden scrollbar-thin bg-slate-50">
        <div class="p-6">
          <slot />
        </div>
      </main>
    </div>
    <!-- Global Notifications -->
    <Toaster />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useCompany } from '@/composables/useCompany'
import { cn } from '@/lib/utils'

// Components
import Button from '@/components/ui/Button.vue'
import Toaster from '@/components/ui/Toaster.vue'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuGroup,
} from '@/components/ui/dropdown-menu'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import SidebarNav from '@/components/SidebarNav.vue'

// Icons
import {
  Menu,
  Layers,
  User,
  Settings,
  LogOut,
  Building2,
  ChevronsUpDown,
  Check,
  Plus
} from 'lucide-vue-next'

const router = useRouter()
const authStore = useAuthStore()
const { companies, selectedCompanyId, loadingCompanies, loadCompanies, setCompany } = useCompany()

// Props
const props = defineProps({
  initialCollapsed: {
    type: Boolean,
    default: false
  }
})

// Sidebar state
const sidebarOpen = ref(false)
const isHoveringSidebar = ref(false)

const savedCollapsedState = localStorage.getItem('sidebar_collapsed')
const sidebarCollapsed = ref(savedCollapsedState ? savedCollapsedState === 'true' : props.initialCollapsed)

// User data
const userName = computed(() => authStore.user?.name || 'Usuario')
const userEmail = computed(() => authStore.user?.email || '')
const userAvatar = computed(() => authStore.user?.avatar || '')
const userInitials = computed(() => {
  const name = userName.value
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
})

const selectedCompanyName = computed(() => {
  const company = companies.value.find(c => c.id === selectedCompanyId.value)
  return company ? (company.nombre_comercial || company.razon_social) : 'Seleccione empresa'
})

const selectedCompanyInitials = computed(() => {
  const name = selectedCompanyName.value
  return name.charAt(0).toUpperCase()
})

// Methods
const toggleSidebar = () => {
  if (window.innerWidth >= 768) {
    sidebarCollapsed.value = !sidebarCollapsed.value
    localStorage.setItem('sidebar_collapsed', sidebarCollapsed.value)
  } else {
    sidebarOpen.value = !sidebarOpen.value
  }
}

const closeSidebar = () => {
  sidebarOpen.value = false
}

const navigateToProfile = () => {
  router.push('/profile')
}

const navigateToSettings = () => {
  router.push('/settings')
}

const navigateToCompanies = () => {
  router.push('/companies')
}

const logout = async () => {
  try {
    await authStore.logout()
    // El store ya se encarga de la redirección
  } catch (error) {
    console.error('Error durante logout:', error)
    // En caso de error, limpiar y redirigir de todas formas
    localStorage.removeItem('auth_token')
    localStorage.removeItem('user_data')
    window.location.href = '/login'
  }
}

const handleCompanySelect = (companyId) => {
  if (companyId === selectedCompanyId.value) return
  setCompany(companyId)
  // Recargar la página actual para refrescar los datos con la nueva empresa
  router.go(0)
}

onMounted(async () => {
  await loadCompanies()
})
</script>